import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, ScrollText } from "lucide-react";
import VendeurFilter from "@/components/VendeurFilter";

const TYPE_CLS = {
  vente: "border-emerald-500/40 text-emerald-600 dark:text-emerald-400",
  caisse: "border-sky-500/40 text-sky-600 dark:text-sky-400",
  stock: "border-amber-500/40 text-amber-600 dark:text-amber-400",
  produit: "border-amber-500/40 text-amber-600 dark:text-amber-400",
  membre: "border-purple-500/40 text-purple-600 dark:text-purple-400",
  utilisateur: "border-border text-foreground/80",
  autorisation: "border-red-500/40 text-red-600 dark:text-red-400",
  parametre: "border-border text-muted-foreground",
  securite: "border-red-500/40 text-red-600 dark:text-red-400",
};

export default function Journal() {
  const [data, setData] = useState(null);
  const [type, setType] = useState("");
  const [userId, setUserId] = useState("");
  const [date, setDate] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    const params = {};
    if (type) params.type = type;
    if (userId) params.user_id = userId;
    if (date) params.date = date;
    if (q.trim()) params.q = q.trim();
    api.get("/journal", { params }).then((r) => setData(r.data)).catch(() => toast.error("Erreur de chargement"));
  }, [type, userId, date, q]);
  useEffect(() => { load(); }, [load]);

  const inputCls = "bg-background border-border text-foreground";

  return (
    <div className="space-y-6 animate-fade-up" data-testid="journal-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground">Journal d'activité</h1>
        <p className="text-sm text-muted-foreground mt-1">Qui a fait quoi : ventes, caisse, stock, membres, autorisations</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une action…" data-testid="journal-search-input" className={`pl-9 w-64 ${inputCls}`} />
        </div>
        <Select value={type || "tous"} onValueChange={(v) => setType(v === "tous" ? "" : v)}>
          <SelectTrigger data-testid="journal-type-select" className={`w-44 ${inputCls}`}><SelectValue /></SelectTrigger>
          <SelectContent className="bg-card border-border text-foreground">
            <SelectItem value="tous">Tous les types</SelectItem>
            {Object.entries(data?.types || {}).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <VendeurFilter value={userId} onChange={setUserId} testid="journal-user-select" />
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="journal-date-input" className={`w-44 ${inputCls}`} />
        <Badge variant="outline" className="border-border text-muted-foreground" data-testid="journal-count">{data ? data.entries.length : 0} entrée(s)</Badge>
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-border">
                <TableHead className="text-muted-foreground">Date / heure</TableHead>
                <TableHead className="text-muted-foreground">Utilisateur</TableHead>
                <TableHead className="text-muted-foreground">Type</TableHead>
                <TableHead className="text-muted-foreground">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data && data.entries.length === 0 && (
                <TableRow className="border-border"><TableCell colSpan={4} className="text-center text-muted-foreground py-8" data-testid="journal-empty">Aucune activité enregistrée</TableCell></TableRow>
              )}
              {data?.entries.map((e) => (
                <TableRow key={e.id} className="border-border" data-testid={`journal-row-${e.id}`}>
                  <TableCell className="text-sm text-foreground/80 whitespace-nowrap">{fmtDate(e.created_at)}</TableCell>
                  <TableCell className="text-sm font-medium text-foreground">{e.user_nom}</TableCell>
                  <TableCell><Badge variant="outline" className={TYPE_CLS[e.type] || "border-border"}>{data.types[e.type] || e.type}</Badge></TableCell>
                  <TableCell className="text-sm text-foreground/90">
                    <span className="flex items-start gap-2"><ScrollText className="w-3.5 h-3.5 mt-0.5 text-muted-foreground shrink-0" />{e.action}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
